import { UnsavedChangesProtection } from 'v5/patterns/forms/unsaved-changes/UnsavedChangesProtection';
import { Dialog, DialogContentHeadless } from '../../design-system/Dialog';
import { DuplicateCallsheetDialogContent } from './DuplicateCallsheetDialogContent';
import { MountElementProps } from '../../messages/mount-element';
import type { DuplicateCallsheetFormData } from './types';
import { WithCallsheetService } from 'v5/services/io/CallsheetService/CallsheetService';
import { WithProductionService } from 'v5/services/io/ProductionService/ProductionService';
import { WithServices } from 'v5/platform/ts/services';
import { Callsheet } from '__types__';

export interface DuplicateCallsheetDialogProps
  extends WithServices<[WithCallsheetService, WithProductionService]> {
  /** The original callsheet to duplicate */
  originalCallsheet: Callsheet;
  onSubmit?: (data: DuplicateCallsheetFormData) => Promise<void> | void;
}

/**
 * Duplicate Callsheet Dialog - v5 implementation
 *
 * Pure dialog component without trigger. Designed to be used with MountElementHandler.
 * Wraps the callsheet duplication dialog with unsaved changes protection.
 *
 * Usage via UserFlowService:
 * ```
 * myServices.userFlowService.openDuplicateCallsheetDialog(workspace, callsheetId);
 * ```
 */
export function DuplicateCallsheetDialog({
  open,
  onOpenChange,
  onSubmit,
  services,
  originalCallsheet
}: DuplicateCallsheetDialogProps & MountElementProps) {
  return (
    <UnsavedChangesProtection
      open={open}
      onOpenChange={onOpenChange}
      beforeUnloadMessage="You have unsaved changes in the callsheet duplication form!"
    >
      {dialogProps => (
        <Dialog {...dialogProps}>
          <DialogContentHeadless>
            <DuplicateCallsheetDialogContent
              open={dialogProps.open}
              onSubmit={onSubmit}
              services={services}
              originalCallsheet={originalCallsheet}
            />
          </DialogContentHeadless>
        </Dialog>
      )}
    </UnsavedChangesProtection>
  );
}
